import { FieldTypes } from '../../constants/FieldTypes'
import {
  CheckboxField,
  DropdownField,
  FieldMap,
  NumberField,
  TextAreaField,
} from '../../types/forms/fields'

export const PH_WARNING_THRESHOLD = 7.2
export const PH_ALERT_THRESHOLD = 7.0
export const PCO2_LOW = 35
export const PCO2_HIGH = 65
export const BASE_EXCESS_LOW = -10

const indicationField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Clinical indication for cord blood gases',
}

const sampleTypeField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Sample Type',
  inputType: 'radio',
  options: [
    { id: 'arterial', text: 'Arterial' },
    { id: 'venous', text: 'Venous' },
    { id: 'both', text: 'Both' },
  ],
  optional: true,
}

const phField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'pH',
  min: 0,
  max: 14,
  step: 0.01,
  precision: 2,
  optional: true,
  validate: v => {
    if (v === undefined || v === null) return undefined
    if (v < PH_ALERT_THRESHOLD)
      return `Severe acidosis (pH < ${PH_ALERT_THRESHOLD}) — immediate neonatal alert`
    if (v < PH_WARNING_THRESHOLD)
      return `Low pH (< ${PH_WARNING_THRESHOLD}) — clinical review required`
    return undefined
  },
}

const pco2Field: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'pCO₂',
  unit: 'mmHg',
  min: 0,
  max: 200,
  step: 0.1,
  precision: 1,
  optional: true,
  validate: v => {
    if (v === undefined || v === null) return undefined
    if (v < PCO2_LOW || v > PCO2_HIGH)
      return `pCO₂ outside normal arterial range (${PCO2_LOW}–${PCO2_HIGH} mmHg)`
    return undefined
  },
}

const po2Field: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'pO₂',
  unit: 'mmHg',
  min: 0,
  max: 200,
  step: 0.1,
  precision: 1,
  optional: true,
}

const baseExcessField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Base Excess',
  unit: 'mEq/L',
  min: -30,
  max: 30,
  step: 0.1,
  precision: 1,
  optional: true,
  validate: v => {
    if (v === undefined || v === null) return undefined
    if (v < BASE_EXCESS_LOW) return `Base excess < ${BASE_EXCESS_LOW} — metabolic acidosis`
    return undefined
  },
}

const bicarbonateField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Bicarbonate',
  unit: 'mEq/L',
  min: 0,
  max: 60,
  step: 0.1,
  precision: 1,
  optional: true,
}

const labReviewedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Lab reviewed and acknowledged',
}

export const cordBloodGasesField: FieldMap = {
  name: 'Cord Blood Gases',
  children: {
    indication: indicationField,
    sampleType: sampleTypeField,
    ph: phField,
    pco2: pco2Field,
    po2: po2Field,
    baseExcess: baseExcessField,
    bicarbonate: bicarbonateField,
    labReviewed: labReviewedField,
  },
}
